import { useDialogPortal, useVoteValidation, type LockedPositionStore, type StakingPositionStore } from "@/hooks";
import { convertWeekStringToDurationNumber } from "@/utils";
import { useCallback, useMemo } from "react";
import { isLockedPosition, isStakedPosition } from "../portfolio-positions.utils";

type Position = LockedPositionStore | StakingPositionStore;

type UsePortfolioPositionRowArgs = {
    position: Position;
    positionType: "locked" | "staked";
};

export const usePortfolioPositionRow = ({ position, positionType }: UsePortfolioPositionRowArgs) => {
    const { openChangeDurationFormDialog } = useDialogPortal();
    const { alreadyVoted, unwindingEpochs } = useVoteValidation();

    const canChangeDuration = useCallback(
        (value: Position) => {
            if (!isLockedPosition(value)) {
                return false;
            }
            const parseLockDurationNumber = Number(value.lockDuration.split("w")[0]);
            return parseLockDurationNumber < 13 && (unwindingEpochs !== parseLockDurationNumber || !alreadyVoted);
        },
        [alreadyVoted, unwindingEpochs],
    );

    const isManageDisabled = useMemo(() => {
        if (positionType !== "locked") {
            return true;
        }
        return !canChangeDuration(position);
    }, [canChangeDuration, position, positionType]);

    const handleManage = useCallback(() => {
        if (positionType !== "locked") {
            return;
        }
        if (!isLockedPosition(position) || !canChangeDuration(position)) {
            return;
        }

        openChangeDurationFormDialog({
            existingLockAmount: Number(position.lockedBalance),
            existingDuration: convertWeekStringToDurationNumber(position.lockDuration),
        });
    }, [canChangeDuration, openChangeDurationFormDialog, position, positionType]);

    const lockDurationWeeks = useMemo(() => {
        if (!isLockedPosition(position)) {
            return null;
        }
        const weeks = Number(position.lockDuration.split("w")[0]);
        return Number.isNaN(weeks) ? null : weeks;
    }, [position]);

    const durationText = useMemo(() => {
        if (isLockedPosition(position)) {
            return lockDurationWeeks === null ? "N/A" : `${lockDurationWeeks}w`;
        }
        if (isStakedPosition(position)) {
            return "—";
        }
        return "—";
    }, [lockDurationWeeks, position]);

    return {
        durationText,
        isManageDisabled,
        handleManage,
    };
};
